/**
 * TabSwitchTimelineChart (Plan 03-07 / 5.0i / D4) — inline-SVG sparkline of a
 * candidate's tab-switch events. No external chart lib (keeps the admin bundle lean).
 *
 * x-axis: occurred_at normalized across the event span. y: blur=top, focus=bottom.
 * Long blurs (duration_seconds > 30) render as red dots (the candidate may have been
 * looking something up); short ones gray. Accessible: aria-label on the <svg> root +
 * a per-dot <title> for screen-reader/hover.
 */
import { useTranslation } from "react-i18next";

import type { TabSwitchEventRow } from "./api";

const W = 280;
const H = 48;
const PAD = 8;
const Y_BLUR = PAD;
const Y_FOCUS = H - PAD;
const LONG_SWITCH_SECONDS = 30;

export function TabSwitchTimelineChart({
  events,
}: {
  events: TabSwitchEventRow[];
}) {
  const { t } = useTranslation("admin-candidates");

  if (!events || events.length === 0) {
    return (
      <p className="text-sm text-muted-foreground">{t("flags.no_tab_switches")}</p>
    );
  }

  const times = events.map((e) => new Date(e.occurred_at).getTime());
  const min = Math.min(...times);
  const max = Math.max(...times);
  const span = max - min || 1;
  const xOf = (ms: number) => PAD + ((ms - min) / span) * (W - 2 * PAD);
  const yOf = (e: TabSwitchEventRow) => (e.event_type === "blur" ? Y_BLUR : Y_FOCUS);

  return (
    <svg
      role="img"
      aria-label={t("flags.tab_switch_timeline")}
      viewBox={`0 0 ${W} ${H}`}
      width="100%"
      height={H}
      style={{ maxWidth: W }}
    >
      {/* baseline */}
      <line x1={PAD} y1={H / 2} x2={W - PAD} y2={H / 2} stroke="#e5e7eb" strokeWidth={1} />
      {/* connecting segments between consecutive events */}
      {events.slice(1).map((e, i) => {
        const prev = events[i];
        return (
          <line
            key={`seg-${e.id}`}
            x1={xOf(times[i])}
            y1={yOf(prev)}
            x2={xOf(times[i + 1])}
            y2={yOf(e)}
            stroke="#9ca3af"
            strokeWidth={1}
          />
        );
      })}
      {/* event dots */}
      {events.map((e, i) => {
        const isLong = (e.duration_seconds ?? 0) > LONG_SWITCH_SECONDS;
        return (
          <circle
            key={e.id}
            cx={xOf(times[i])}
            cy={yOf(e)}
            r={isLong ? 4 : 2}
            fill={isLong ? "#dc2626" : "#6b7280"}
          >
            <title>
              {`${e.event_type} · ${e.duration_seconds ?? 0}s`}
            </title>
          </circle>
        );
      })}
    </svg>
  );
}
